import { motion } from "framer-motion";
import { ArrowUpRight, Linkedin } from "lucide-react";
import { LOGO_URL, OWNER_EMAIL, NAV_LINKS } from "@/lib/content";
import { scrollToId } from "@/lib/scroll";

const LINKEDIN_URL = "https://www.linkedin.com/company/brain-brush/?viewAsMember=true";

export const Footer = () => {
    return (
        <footer className="relative overflow-hidden bg-[#090A0F] text-white">
            <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full bg-violet-600/20 blur-[140px] pointer-events-none" />
            <div className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] rounded-full bg-pink-600/15 blur-[140px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-20 lg:pt-28 pb-10">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center"
                >
                    <h2 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-7xl" data-testid="footer-cta-heading">
                        Let's build<br />
                        <span className="gradient-text">together.</span>
                    </h2>
                    <button
                        data-testid="footer-cta-start-project"
                        onClick={() => scrollToId("contact")}
                        className="mt-9 gradient-bg text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2.5 shadow-2xl shadow-violet-500/30 hover:scale-[1.04] transition-all duration-300"
                    >
                        Start a Project <ArrowUpRight size={17} />
                    </button>
                </motion.div>

                <div className="mt-20 pt-10 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-10">
                    <div>
                        <div className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="Brain Brush logo" className="h-10 w-10 rounded-lg" />
                            <span className="font-display font-bold text-lg">Brain Brush</span>
                        </div>
                        <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-xs">
                            A digital design and development agency transforming ideas into beautiful, intelligent and impactful products.
                        </p>
                        <a
                            data-testid="footer-email-link"
                            href={`mailto:${OWNER_EMAIL}`}
                            className="mt-4 inline-block text-sm font-medium text-slate-300 hover:text-white transition-colors"
                        >
                            {OWNER_EMAIL}
                        </a>
                    </div>

                    <div>
                        <div className="font-mono-brand text-[11px] tracking-[0.22em] uppercase text-slate-500 font-semibold">Navigate</div>
                        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 max-w-xs">
                            {NAV_LINKS.map((link) => (
                                <button
                                    key={link.id}
                                    data-testid={`footer-nav-${link.id}`}
                                    onClick={() => scrollToId(link.id)}
                                    className="text-left text-sm text-slate-400 hover:text-white transition-colors"
                                >
                                    {link.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="font-mono-brand text-[11px] tracking-[0.22em] uppercase text-slate-500 font-semibold">Follow us</div>
                        <div className="mt-4 flex gap-3">
                            <a
                                data-testid="footer-social-linkedin"
                                href={LINKEDIN_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Brain Brush on LinkedIn"
                                className="h-10 w-10 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-slate-300 hover:text-white hover:border-violet-400 hover:bg-violet-500/20 transition-all duration-300"
                            >
                                <Linkedin size={16} />
                            </a>
                        </div>
                        <p className="mt-5 text-xs text-slate-500 leading-relaxed">
                            Follow Brain Brush on LinkedIn — or reach us directly by email.
                        </p>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-xs text-slate-500" data-testid="footer-copyright">
                        © 2026 Brain Brush. All rights reserved.
                    </div>
                    <div className="font-mono-brand text-[11px] tracking-[0.18em] uppercase text-slate-600">
                        Designed & built by Brain Brush
                    </div>
                </div>
            </div>
        </footer>
    );
};
